import { Link } from 'expo-router';
import { Image, Platform, Pressable, StyleSheet, View } from 'react-native';

import { Button, Pill, Text } from '@/components/ui';
import { MoodSection, fluidFontSize, fluidLineHeight, useTheme, palette } from '@/theme';
import type { EventItem } from '@/content/events';

const heroBgMap: Record<EventItem['hero'], string> = {
  wine: palette.wine,
  verde: palette.verdeSlate,
  midnight: palette.night,
};

export function EventPoster({ event }: { event: EventItem }) {
  const theme = useTheme();
  const isMidnight = event.hero === 'midnight';

  const body = (
    <View
      style={[
        styles.card,
        {
          backgroundColor: heroBgMap[event.hero],
          borderRadius: theme.radii.md,
        },
        isMidnight && {
          shadowColor: palette.wine,
          shadowOpacity: 0.5,
          shadowRadius: 60,
          shadowOffset: { width: 0, height: 20 },
        },
      ]}
    >
      {event.image ? (
        <View style={styles.posterImage}>
          <Image
            source={event.image}
            style={styles.posterImageInner}
            resizeMode="cover"
            accessibilityLabel={event.title}
          />
          <View style={styles.posterImageBadge}>
            <Pill variant="invert">{event.ord}</Pill>
          </View>
        </View>
      ) : (
        <View style={styles.poster}>
          <View style={styles.posterHead}>
            <Pill variant="invert">{event.ord}</Pill>
            <Text variant="eyebrow" style={{ color: 'rgba(245,240,255,0.78)' }}>
              Featured
            </Text>
          </View>
          <Text
            style={{
              fontFamily: theme.fontFamily.display,
              fontSize: fluidFontSize(36, 7.5, 72) as any,
              lineHeight: fluidLineHeight(1.05, 76) as any,
              color: palette.paper,
              marginTop: 32,
              letterSpacing: -1.5,
            }}
          >
            {event.title}
          </Text>
          {event.guests ? (
            <Text
              style={{
                fontFamily: theme.fontFamily.displayItalic,
                fontStyle: 'italic',
                color: isMidnight ? palette.ember : palette.emberSoft,
                fontSize: 28,
                lineHeight: 32,
                marginTop: 16,
              }}
            >
              {event.guests}
            </Text>
          ) : null}
          <View
            style={[
              styles.posterMoon,
              { borderColor: 'rgba(245,240,255,0.18)' },
            ]}
          />
        </View>
      )}

      <View style={[styles.meta, { borderTopColor: 'rgba(245,240,255,0.16)' }]}>
        <View style={styles.metaCol}>
          <Text variant="eyebrow" style={{ color: 'rgba(245,240,255,0.6)' }}>Datum</Text>
          <Text variant="h4" style={{ color: palette.paper, marginTop: 4 }}>{event.dateLabel}</Text>
        </View>
        <View style={styles.metaCol}>
          <Text variant="eyebrow" style={{ color: 'rgba(245,240,255,0.6)' }}>Ort</Text>
          <Text variant="h4" style={{ color: palette.paper, marginTop: 4 }}>{event.location}</Text>
        </View>
        {event.format ? (
          <View style={styles.metaCol}>
            <Text variant="eyebrow" style={{ color: 'rgba(245,240,255,0.6)' }}>Format</Text>
            <Text variant="h4" style={{ color: palette.paper, marginTop: 4 }}>{event.format}</Text>
          </View>
        ) : null}
      </View>

      <View style={styles.body}>
        <Text style={{ color: 'rgba(245,240,255,0.85)', maxWidth: 640, fontSize: 17, lineHeight: 26 }}>
          {event.body}
        </Text>
        <View style={styles.highlights}>
          {event.highlights.map((h, i) => (
            <View key={i} style={styles.highlightRow}>
              <Text variant="meta" style={{ color: 'rgba(245,240,255,0.6)' }}>
                0{i + 1}
              </Text>
              <Text style={{ color: palette.paper, fontSize: 17, lineHeight: 24, flex: 1 }}>{h}</Text>
            </View>
          ))}
        </View>
        <View style={{ marginTop: 32 }}>
          <Button variant="ember" size="lg" trailingArrow>
            Tickets
          </Button>
        </View>
      </View>
    </View>
  );

  if (isMidnight) {
    return <MoodSection mood="midnight" style={{ backgroundColor: 'transparent' }}>{body}</MoodSection>;
  }
  return body;
}

const styles = StyleSheet.create({
  card: {
    overflow: 'hidden',
  },
  poster: {
    padding: 40,
    paddingBottom: 64,
    minHeight: 360,
    position: 'relative',
    overflow: 'hidden',
  },
  posterImage: {
    width: '100%',
    maxWidth: 560,
    aspectRatio: 1,
    alignSelf: 'center',
    position: 'relative',
    overflow: 'hidden',
    marginTop: 24,
    marginBottom: 8,
  },
  posterImageInner: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  posterImageBadge: {
    position: 'absolute',
    top: 24,
    left: 24,
  },
  posterHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  posterMoon: {
    position: 'absolute',
    width: 480,
    height: 480,
    right: -160,
    bottom: -200,
    borderRadius: 240,
    borderWidth: 1,
  },
  meta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
  },
  metaCol: {
    flex: 1,
    minWidth: 200,
    padding: 24,
  },
  body: {
    padding: 40,
  },
  highlights: {
    marginTop: 24,
    gap: 16,
  },
  highlightRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
});
